import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import FilmCard from '@/components/feature/FilmCard';
import EmptyState from '@/components/base/EmptyState';
import Pagination from '@/components/base/Pagination';
import FilterPanel from '@/pages/category/components/FilterPanel';
import { films } from '@/mocks/films';
import {
  PAGE_SIZES,
  SORT_OPTIONS,
  filterFilms,
  paginate,
  sortFilms,
  typeLabel,
} from '@/lib/film';
import type { FilmType } from '@/types/film';
import type { SortKey } from '@/lib/film';
import { useSeo } from '@/hooks/useSeo';
import { useState } from 'react';

const parseSize = (value: string | null) => {
  const parsed = Number(value);
  return PAGE_SIZES.includes(parsed) ? parsed : 8;
};

const parseSort = (value: string | null): SortKey =>
  SORT_OPTIONS.some((option) => option.value === value) ? (value as SortKey) : 'latest';

export default function CategoryPage() {
  const [params, setParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const type: FilmType = params.get('type') === 'tv' ? 'tv' : 'movie';
  const genre = params.get('genre') ?? undefined;
  const region = params.get('region') ?? undefined;
  const yearParam = params.get('year');
  const year = yearParam && !Number.isNaN(Number(yearParam)) ? Number(yearParam) : undefined;
  const sort = parseSort(params.get('sort'));
  const size = parseSize(params.get('size'));
  const pageParam = Number(params.get('page') ?? '1');
  const page = Number.isNaN(pageParam) ? 1 : pageParam;

  const title = type === 'movie' ? '电影分类' : '电视剧分类';

  useSeo({
    title: `${title} - 青青草影视资源平台`,
    description: `青青草${title}页，收录${typeLabel(type)}作品 ${films.filter((film) => film.type === type).length} 部，支持按类型、地区、年份筛选，按最新、热度、评分排序，快速找到想看的作品。`,
    keywords: `青青草,${title},${typeLabel(type)}推荐,影视筛选,在线影视资源`,
  });

  const filtered = filterFilms(films, { type, genre, region, year });
  const sorted = sortFilms(filtered, sort);
  const result = paginate(sorted, page, size);
  const hasFilter = Boolean(genre || region || year);

  const updateParams = (patch: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null) next.delete(key);
      else next.set(key, value);
    });
    if (resetPage) next.delete('page');
    setParams(next, { replace: true });
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    next.set('type', type);
    setParams(next, { replace: true });
  };

  const switchType = (nextType: FilmType) => {
    const next = new URLSearchParams();
    next.set('type', nextType);
    setParams(next, { replace: true });
  };

  const filterProps = {
    genre,
    region,
    year,
    onGenreChange: (value?: string) => updateParams({ genre: value ?? null }),
    onRegionChange: (value?: string) => updateParams({ region: value ?? null }),
    onYearChange: (value?: number) => updateParams({ year: value ? `${value}` : null }),
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-portal px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-3 flex items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">{title}</span>
            {hasFilter && (
              <>
                <i className="ri-arrow-right-s-line" />
                <span className="text-accent-700">已筛选</span>
              </>
            )}
          </nav>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{title}</h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">
                {type === 'movie'
                  ? '收录院线新片、类型佳作与口碑剧情片，按类型、地区、年份组合筛选，找到今晚想看的两个多小时。'
                  : '收录热播剧集、单元短剧与高分长篇，支持按题材与年份筛选，追更和补剧都不迷路。'}
              </p>
            </div>
            <div className="flex items-center gap-1 rounded-full bg-background-200/70 p-1">
              {(['movie', 'tv'] as FilmType[]).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => switchType(item)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    type === item
                      ? 'bg-primary-600 font-medium text-background-50'
                      : 'text-foreground-600 hover:text-primary-700'
                  }`}
                >
                  {item === 'movie' ? '电影' : '电视剧'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-portal px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          <aside className="w-full shrink-0 lg:w-[260px]">
            <div className="hidden rounded-lg border border-background-200 bg-background-50 p-4 lg:block">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-sm font-bold text-foreground-900">筛选条件</h2>
                {hasFilter && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="cursor-pointer text-xs text-accent-700 transition-colors hover:text-accent-800"
                  >
                    重置
                  </button>
                )}
              </div>
              <FilterPanel {...filterProps} />
            </div>

            <div className="lg:hidden">
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-800"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-base text-primary-600" />
                  筛选条件
                  {hasFilter && (
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] text-accent-800">
                      已选 {[genre, region, year].filter(Boolean).length} 项
                    </span>
                  )}
                </span>
                <i className={`text-lg ${filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
              </button>
              {filterOpen && (
                <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <FilterPanel {...filterProps} />
                  {hasFilter && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="mt-4 w-full cursor-pointer rounded-md border border-background-300 py-2 text-xs text-foreground-700"
                    >
                      重置全部筛选
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 hidden rounded-lg border border-background-200 bg-secondary-50 p-4 lg:block">
              <p className="text-xs font-semibold text-secondary-900">小提示</p>
              <p className="mt-2 text-xs leading-relaxed text-secondary-700">
                同时选择类型与年份可以快速缩小范围；点已选中的标签可以取消该条件。
              </p>
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">
                共 <span className="font-semibold text-primary-700">{result.total}</span> 部作品
                {hasFilter && <span className="ml-2 text-xs text-foreground-400">（已应用筛选）</span>}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-xs text-foreground-500">
                  排序
                  <select
                    value={sort}
                    onChange={(event) => updateParams({ sort: event.target.value })}
                    className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-800 outline-none focus:border-primary-400"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-xs text-foreground-500">
                  每页
                  <select
                    value={size}
                    onChange={(event) => updateParams({ size: event.target.value })}
                    className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-800 outline-none focus:border-primary-400"
                  >
                    {PAGE_SIZES.map((option) => (
                      <option key={option} value={option}>
                        {option} 部
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {result.items.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                  {result.items.map((film) => (
                    <FilmCard key={film.id} film={film} />
                  ))}
                </div>
                <div className="mt-8">
                  <Pagination
                    page={result.page}
                    totalPages={result.totalPages}
                    onChange={(nextPage) => updateParams({ page: `${nextPage}` }, false)}
                  />
                </div>
              </>
            ) : (
              <EmptyState
                onAction={resetFilters}
                secondaryLabel="回到首页"
                secondaryTo="/"
                description="当前组合条件下没有匹配的作品，试着放宽类型或年份，或者直接重置筛选。"
              />
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}